import NetworkIcon from "@core/network-icon";
import { AITimelineContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";

interface TimelineTemplateProps {
  content: AITimelineContent;
  onUpdate: (newContent: AITimelineContent) => void;
}

export function TimelineTemplate({ content, onUpdate }: TimelineTemplateProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");

  // Handle event update
  const handleEventUpdate = (
    index: number,
    data: { title?: string; description?: string },
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      if (data.title !== undefined) {
        newContent.steps[index].title = data.title;
      }
      if (data.description !== undefined) {
        newContent.events[index].description = data.description;
      }
      onUpdate(newContent);
    }
  };

  // Ensure events array exists and is not empty
  if (!content.steps || content.steps.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        No timeline events to display.
      </div>
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  return (
    <div className="p-6 bg-base-100 rounded-lg overflow-hidden">
      <motion.div
        className="relative flex justify-between items-stretch"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="absolute top-8 left-0 right-0 mx-auto w-[calc(100%-4rem)] h-0.5 bg-gray-300 dark:bg-gray-600 mt-3"></div>

        {content.steps.map((event, index) => {
          const isEditingTitle =
            editingIndex === index && editingField === "title";
          const isEditingDescription =
            editingIndex === index && editingField === "description";

          const iconBgColor =
            event.color ||
            content.genericColors?.[index % content.genericColors.length] ||
            "#9CA3AF"; // Default gray-400

          return (
            <motion.div
              key={`event-${index}`}
              className="relative z-10 flex flex-col items-center px-2"
              variants={itemVariants}
              style={{ flex: 1 }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-2 shadow-lg relative z-10"
                style={{ backgroundColor: iconBgColor }}
                aria-label={event.icon_description || event.title}
              >
                {event.iconId ? (
                  <NetworkIcon
                    value={event.icon_description ?? event.iconId}
                    type="LUCIDE"
                    className="w-8 h-8"
                  />
                ) : (
                  "?"
                )}
              </div>

              {isEditingTitle ? (
                <input
                  autoFocus
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== event.title) {
                      handleEventUpdate(index, { title: tempValue });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                  className="font-semibold text-base-content h-10 flex items-center focus:outline-none focus:ring-2 focus:ring-primary bg-base-100 rounded px-1"
                />
              ) : (
                <h4
                  contentEditable={false}
                  className="font-semibold text-base-content h-10 flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:bg-base-300 rounded px-1"
                  onClick={() => {
                    setEditingIndex(index);
                    setEditingField("title");
                    setTempValue(event.title);
                  }}
                >
                  {event.title}
                </h4>
              )}

              <div className="relative h-8 flex items-center mt-1">
                <div
                  className={`w-3 h-3 rounded-full shadow-sm bg-white border-2 dark:bg-gray-800`}
                  style={{ borderColor: iconBgColor }}
                ></div>
              </div>

              <div className="text-center text-sm text-base-content/80 mt-2">
                {isEditingDescription ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingIndex(null);
                      setEditingField(null);
                      if (tempValue !== event.description) {
                        handleEventUpdate(index, { description: tempValue });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    onFocus={(e) => {
                      // auto height
                      e.currentTarget.style.height = "auto";
                      e.currentTarget.style.height =
                        e.currentTarget.scrollHeight + "px";
                    }}
                    rows={2}
                    className="w-full focus:outline-none focus:ring-2 focus:ring-primary bg-base-100 rounded px-1"
                  />
                ) : (
                  <p
                    contentEditable={false}
                    className="focus:outline-none focus:ring-2 focus:ring-primary focus:bg-base-300 rounded px-1 cursor-text"
                    onClick={() => {
                      setEditingIndex(index);
                      setEditingField("description");
                      setTempValue(event.description);
                    }}
                  >
                    {event.description}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
